import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, TextField, Button,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Alert, CircularProgress, InputAdornment, IconButton,
  Divider, FormControl, InputLabel, Select, MenuItem, Tooltip,
} from '@mui/material';
import {
  Description as DocIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  History as HistoryIcon,
  Badge as CertIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import { DocumentSecurity } from '../../services/documentSecurity';

const DOC_TYPE_LABELS = {
  convocation:          'Convocation d\'examen',
  certificat_scolarite: 'Certificat de Scolarité',
  releve_notes:         'Relevé de Notes',
  attestation:          'Attestation',
  attestation_scolarite:'Attestation de Scolarité',
  unknown:              'Autre',
};
const DOC_TYPE_COLORS = {
  convocation:          '#7c3aed',
  certificat_scolarite: '#2563eb',
  releve_notes:         '#16a34a',
  attestation_scolarite:'#2563eb',
  unknown:              '#64748b',
};

// ── Download Logs tab ─────────────────────────────────────────────────────────
const LogsTab = () => {
  const [logs, setLogs]                 = useState([]);
  const [uniqueStudents, setUnique]     = useState(0);
  const [totalDownloads, setTotalDl]   = useState(0);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [search, setSearch]             = useState('');
  const [docType, setDocType]           = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (search.trim()) params.cod_etu = search.trim();
      if (docType)        params.doc_type = docType;
      const data = await adminAPI.getDocumentLogs(params);
      setLogs(data.logs || []);
      setUnique(data.unique_students || 0);
      setTotalDl(data.total_downloads || 0);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [search, docType]);

  useEffect(() => { load(); }, []);

  const handleSearch = (e) => { e.preventDefault(); load(); };

  const studentName = (log) =>
    log.lib_nom_pat_ind && log.lib_pr1_ind
      ? `${log.lib_nom_pat_ind} ${log.lib_pr1_ind}`
      : log.lib_nom_ind_arb && log.lib_prn_ind_arb
        ? `${log.lib_nom_ind_arb} ${log.lib_prn_ind_arb}`
        : '—';

  return (
    <Box>
      {/* Summary chips */}
      {!loading && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
          <Paper elevation={0} sx={{ px: 2.5, py: 1.5, borderRadius: 3, border: '1.5px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PersonIcon sx={{ color: '#2563eb', fontSize: 22 }} />
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ color: '#2563eb', lineHeight: 1 }}>{uniqueStudents}</Typography>
              <Typography variant="caption" color="text.secondary">étudiants uniques</Typography>
            </Box>
          </Paper>
          <Paper elevation={0} sx={{ px: 2.5, py: 1.5, borderRadius: 3, border: '1.5px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DownloadIcon sx={{ color: '#16a34a', fontSize: 22 }} />
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ color: '#16a34a', lineHeight: 1 }}>{totalDownloads}</Typography>
              <Typography variant="caption" color="text.secondary">téléchargements au total</Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Filters */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e8ecef', p: 2.5, mb: 3 }}>
        <form onSubmit={handleSearch}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <TextField
              size="small" label="Code / Nom étudiant" value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}><ClearIcon sx={{ fontSize: 16 }} /></IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 220 }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Type de document</InputLabel>
              <Select value={docType} onChange={e => setDocType(e.target.value)} label="Type de document">
                <MenuItem value="">Tous</MenuItem>
                {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button type="submit" variant="contained" startIcon={<SearchIcon />} sx={{ borderRadius: 2, boxShadow: 'none' }}>
              Rechercher
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} sx={{ borderRadius: 2 }}>
              Actualiser
            </Button>
          </Box>
        </form>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e8ecef' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Étudiant</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Document</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Nb téléchargements</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>1er téléchargement</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Dernier téléchargement</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.disabled' }}>Aucun téléchargement trouvé</TableCell></TableRow>
            ) : logs.map((log, i) => (
              <TableRow key={`${log.cod_etu}-${log.doc_type}-${i}`} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                <TableCell sx={{ py: 1.2 }}>
                  <Typography variant="body2" fontWeight={600}>{studentName(log)}</Typography>
                </TableCell>
                <TableCell sx={{ py: 1.2, fontFamily: 'monospace', fontSize: '0.78rem', color: '#475569' }}>
                  {log.cod_etu}
                </TableCell>
                <TableCell sx={{ py: 1.2 }}>
                  <Chip
                    label={DOC_TYPE_LABELS[log.doc_type] || log.doc_type}
                    size="small"
                    sx={{
                      height: 20, borderRadius: 1, fontWeight: 600, fontSize: '0.68rem',
                      bgcolor: (DOC_TYPE_COLORS[log.doc_type] || '#64748b') + '18',
                      color: DOC_TYPE_COLORS[log.doc_type] || '#64748b',
                    }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ py: 1.2 }}>
                  <Chip
                    label={log.download_count}
                    size="small"
                    sx={{
                      fontWeight: 800, fontSize: '0.78rem', minWidth: 32,
                      bgcolor: log.download_count >= 5 ? '#fef3c7' : log.download_count >= 2 ? '#eff6ff' : '#f1f5f9',
                      color:   log.download_count >= 5 ? '#92400e' : log.download_count >= 2 ? '#1d4ed8' : '#475569',
                    }}
                  />
                </TableCell>
                <TableCell sx={{ py: 1.2, fontSize: '0.75rem', color: '#94a3b8' }}>
                  {new Date(log.first_download).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                </TableCell>
                <TableCell sx={{ py: 1.2, fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>
                  {new Date(log.last_download).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ── Generate Certificate tab ──────────────────────────────────────────────────
const GenerateTab = () => {
  const [codEtu, setCodEtu]         = useState('');
  const [student, setStudent]       = useState(null);
  const [searching, setSearching]   = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError]           = useState('');
  const [year, setYear]             = useState('');

  const lookupStudent = async (e) => {
    e.preventDefault();
    if (!codEtu.trim()) return;
    setSearching(true); setError(''); setStudent(null);
    try {
      const data = await adminAPI.getStudentInfoForDoc(codEtu.trim().toUpperCase());
      setStudent(data.student);
      setYear(data.student.enroll_year ? String(data.student.enroll_year) : '');
    } catch (e) {
      setError(e.response?.status === 404 ? 'Étudiant non trouvé' : 'Erreur de recherche');
    } finally {
      setSearching(false);
    }
  };

  // Exact same PDF as generateEnrollmentCertificate in AdministrativeSituation.jsx
  // but uses admin-fetched student data and admin token for signature storage
  const generateCertificate = async () => {
    if (!student) return;
    setGenerating(true); setError('');
    try {
      const jsPDF  = (await import('jspdf')).default;
      await import('jspdf-autotable');

      const doc       = new jsPDF('p', 'mm', 'a4');
      const pageWidth  = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Load Arabic font
      try {
        const fontResponse = await fetch('/fonts/Amiri-Regular.ttf');
        if (fontResponse.ok) {
          const fontBlob = await fontResponse.blob();
          const fontBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(fontBlob);
          });
          doc.addFileToVFS('Amiri-Regular.ttf', fontBase64);
          doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal');
          doc.addFont('Amiri-Regular.ttf', 'Amiri', 'bold');
          doc.setFont('Amiri', 'normal');
        }
      } catch {}

      doc.setTextColor(0, 0, 0);
      const selectedYr      = year || String(student.enroll_year || new Date().getFullYear());
      const formattedYear   = `${selectedYr}/${parseInt(selectedYr) + 1}`;

      // Build a user-like object from admin-fetched student data
      const u = {
        cod_etu:          student.cod_etu,
        nom_complet:      `${student.lib_nom_pat_ind || ''} ${student.lib_pr1_ind || ''}`.trim(),
        cin:              student.cin_ind,
        cod_nne_ind:      student.cod_nne_ind,
        date_naissance:   student.date_nai_ind,
        lieu_naissance:   student.lib_vil_nai_etu,
        cod_sex_etu:      student.cod_sex_etu,
      };

      // ===== HEADER =====
      doc.setFontSize(10);
      doc.text('ROYAUME DU MAROC',                    15, 15);
      doc.text('Université Hassan 1er',                15, 21);
      doc.text('FACULTÉ DES SCIENCES',                 15, 27);
      doc.text('JURIDIQUES ET POLITIQUES -',           15, 33);
      doc.text('SETTAT',                               15, 39);
      doc.setFontSize(8);
      doc.text('Service des Affaires Estudiantines',   15, 45);

      doc.setFontSize(10);
      doc.text('المملكة المغربية',                     pageWidth - 15, 15, { align: 'right' });
      doc.text('جامعة الحسن الأول',                    pageWidth - 15, 21, { align: 'right' });
      doc.text('كلية العلوم القانونية والسياسية',      pageWidth - 15, 27, { align: 'right' });
      doc.text('سطات',                                 pageWidth - 15, 33, { align: 'right' });
      doc.setFontSize(8);
      doc.text('مصلحة الشؤون الطلابية',               pageWidth - 15, 39, { align: 'right' });

      // ===== TITLE =====
      doc.setFontSize(16);
      doc.setFont('Amiri', 'bold');
      doc.text("ATTESTATION D'INSCRIPTION", pageWidth / 2, 65, { align: 'center' });

      // ===== BODY =====
      doc.setFont('Amiri', 'normal');
      doc.setFontSize(10);
      doc.text("La Doyenne de la Faculté des Sciences Juridiques et Politiques - Settat atteste que", 20, 82);
      doc.text("l'étudiant(e) :", 20, 90);

      doc.setFont('Amiri', 'bold');
      doc.text(u.nom_complet || 'N/A', 20, 100);

      doc.setFont('Amiri', 'normal');
      doc.text(`Numéro de la carte d'identité nationale : ${u.cin || 'N/A'}`, 20, 110);
      doc.text(`Code national de l'étudiant(e) : ${u.cod_nne_ind || u.cod_etu || 'N/A'}`, 20, 118);

      let birthText = 'né(e) le ';
      if (u.date_naissance) {
        birthText += new Date(u.date_naissance).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
      } else {
        birthText += '[Date non disponible]';
      }
      if (u.lieu_naissance) birthText += ` à ${u.lieu_naissance} ( MAROC )`;
      else                  birthText += ' ( MAROC )';
      doc.text(birthText, 20, 126);

      doc.text("est régulièrement inscrit(e) à la FACULTÉ DES SCIENCES JURIDIQUES ET POLITIQUES -", 20, 138);
      doc.text(`SETTAT pour l'année universitaire ${formattedYear}.`, 20, 146);

      let programY = 158;
      if (student.lib_etp) {
        doc.text(`Filière : ${student.lib_etp}`, 20, programY);
      }

      // ===== SECURITY FEATURES =====
      const registrations = [{ cod_etp: student.cod_etp, lib_etp: student.lib_etp }];
      const documentData  = {
        studentId:     u.cod_etu,
        semester:      `YEAR_${selectedYr}`,
        timestamp:     new Date().toISOString(),
        registrations: registrations.map(r => ({ cod_etp: r.cod_etp, lib_etp: r.lib_etp })),
      };

      const { signature, qrCodeDataUrl } = await DocumentSecurity.signDocumentAsAdmin(documentData);

      // ===== FOOTER =====
      const footerStartY = 180;
      const currentDate  = new Date();
      const dateText     = `Fait à SETTAT, le ${currentDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`;
      doc.setFontSize(10);
      doc.text(dateText, pageWidth - 20, footerStartY, { align: 'right' });
      doc.setFont('Amiri', 'bold');
      doc.text('La Doyenne de la FSJP - Settat', pageWidth - 20, footerStartY + 8, { align: 'right' });
      doc.setFont('Amiri', 'normal');
      doc.setFontSize(9);
      doc.text(u.cod_etu || '', pageWidth - 20, footerStartY + 16, { align: 'right' });

      // ===== QR CODE =====
      const qrX = 15, qrY = footerStartY - 5, qrSize = 35;
      if (qrCodeDataUrl) {
        try {
          doc.setDrawColor(52, 152, 219);
          doc.setLineWidth(0.5);
          doc.rect(qrX - 1, qrY - 1, qrSize + 2, qrSize + 2);
          doc.addImage(qrCodeDataUrl.split(',')[1], 'PNG', qrX, qrY, qrSize, qrSize);
          doc.setFontSize(7);
          doc.text('Code QR de Vérification',     qrX + qrSize / 2, qrY + qrSize + 4, { align: 'center' });
          doc.text('Scanner pour vérifier',        qrX + qrSize / 2, qrY + qrSize + 8, { align: 'center' });
        } catch {}
      }

      // ===== DIGITAL SIGNATURE BOX =====
      const sigX = 60, sigY = footerStartY + 10, sigWidth = 90, sigHeight = 18;
      doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.3);
      doc.rect(sigX, sigY, sigWidth, sigHeight);
      doc.setFontSize(7); doc.setFont('Amiri', 'bold');
      doc.text('Signature Numérique', sigX + sigWidth / 2, sigY + 8, { align: 'center' });
      if (signature) {
        doc.setFont('Courier', 'normal'); doc.setFontSize(6);
        doc.text(signature, sigX + sigWidth / 2, sigY + 13, { align: 'center' });
      }

      // ===== UNIVERSITY FOOTER =====
      const bottomY = pageHeight - 20;
      doc.setFontSize(8); doc.setFont('Amiri', 'normal');
      doc.text('Adresse : COMPLEXE UNIVERSITAIRE - SETTAT', 15, bottomY);
      doc.text('Tél. : 0523721939  |  FAX : 0523724087', 15, bottomY + 5);
      doc.text('العنوان : المركب الجامعي - سطات', pageWidth - 15, bottomY, { align: 'right' });
      doc.text('الهاتف : 0523721939  |  الفاكس : 0523724087', pageWidth - 15, bottomY + 5, { align: 'right' });
      doc.setFontSize(7); doc.setTextColor(150);
      doc.text('16005333', pageWidth - 15, bottomY + 10, { align: 'right' });
      doc.setTextColor(0);

      doc.save(`Attestation_Scolarite_${u.cod_etu}_${selectedYr}.pdf`);
    } catch (err) {
      setError('Erreur lors de la génération du PDF : ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Recherchez un étudiant par son code Apogée, puis générez son attestation de scolarité.
      </Typography>

      {/* Lookup form */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e8ecef', p: 3, mb: 3 }}>
        <form onSubmit={lookupStudent}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <TextField
              fullWidth size="small" label="Code étudiant (Apogée)" value={codEtu}
              onChange={e => setCodEtu(e.target.value.toUpperCase())}
              placeholder="Ex: 12345678"
              InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
            />
            <Button type="submit" variant="contained" disabled={searching || !codEtu.trim()}
              startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              sx={{ borderRadius: 2, boxShadow: 'none', whiteSpace: 'nowrap' }}>
              Rechercher
            </Button>
          </Box>
        </form>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      {/* Student info card */}
      {student && (
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #2563eb33', p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <CertIcon sx={{ color: '#2563eb' }} />
            <Typography variant="subtitle1" fontWeight={700} color="#2563eb">Étudiant trouvé</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {[
            ['Nom', `${student.lib_nom_pat_ind || ''} ${student.lib_pr1_ind || ''}`.trim()],
            ['اسم', `${student.lib_nom_ind_arb || ''} ${student.lib_prn_ind_arb || ''}`.trim()],
            ['Code Apogée', student.cod_etu],
            ['CIN', student.cin_ind || '—'],
            ['CNE', student.cod_nne_ind || '—'],
            ['Filière', student.lib_etp || '—'],
          ].map(([label, value]) => (
            <Box key={label} sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
              <Typography variant="body2" fontWeight={700} sx={{ minWidth: 110, color: '#475569' }}>{label} :</Typography>
              <Typography variant="body2">{value}</Typography>
            </Box>
          ))}

          <Box sx={{ mt: 2.5, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small" label="Année universitaire" value={year}
              onChange={e => setYear(e.target.value)}
              placeholder="ex: 2025"
              sx={{ width: 160 }}
            />
            <Button
              variant="contained" color="primary"
              startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={generateCertificate} disabled={generating}
              sx={{ borderRadius: 2, boxShadow: 'none' }}
            >
              {generating ? 'Génération…' : 'Générer l\'attestation'}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const AdminDocuments = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1a2a3a 0%, #2c3e50 60%, #3d566e 100%)',
        borderRadius: 4, p: { xs: 2.5, md: 3 }, mb: 3, color: 'white',
        display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <Box sx={{ width: 48, height: 48, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <DocIcon sx={{ fontSize: 26 }} />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700}>Gestion des Documents</Typography>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>Journaux de téléchargement et génération d'attestations</Typography>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e8ecef', overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: '1px solid #e8ecef', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
          <Tab icon={<HistoryIcon />} iconPosition="start" label="Journal des téléchargements" />
          <Tab icon={<CertIcon />}    iconPosition="start" label="Générer une attestation" />
        </Tabs>
        <Box sx={{ p: 3 }}>
          {tab === 0 && <LogsTab />}
          {tab === 1 && <GenerateTab />}
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminDocuments;
